import { useState, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion } from "framer-motion";
import { Home, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useWindowSize } from "@/hooks/useWindowSize";

const PIANO_KEYS = [
  { note: "C", freq: 261.63, color: "white", label: "Do" },
  { note: "C#", freq: 277.18, color: "black" },
  { note: "D", freq: 293.66, color: "white", label: "Re" },
  { note: "D#", freq: 311.13, color: "black" },
  { note: "E", freq: 329.63, color: "white", label: "Mi" },
  { note: "F", freq: 349.23, color: "white", label: "Fa" },
  { note: "F#", freq: 369.99, color: "black" },
  { note: "G", freq: 392.00, color: "white", label: "Sol" },
  { note: "G#", freq: 415.30, color: "black" },
  { note: "A", freq: 440.00, color: "white", label: "La" },
  { note: "A#", freq: 466.16, color: "black" },
  { note: "B", freq: 493.88, color: "white", label: "Si" },
  { note: "C2", freq: 523.25, color: "white", label: "Do'" },
];

const DRUMS = [
  { name: "Kick", emoji: "🥁", freq: 80, type: "sine", dur: 0.3, color: "#FF6347" },
  { name: "Snare", emoji: "🪘", freq: 200, type: "triangle", dur: 0.15, color: "#FFD700" },
  { name: "Hi-Hat", emoji: "🔔", freq: 800, type: "square", dur: 0.05, color: "#87CEEB" },
  { name: "Tom", emoji: "🎵", freq: 150, type: "sine", dur: 0.2, color: "#FF69B4" },
  { name: "Crash", emoji: "💥", freq: 500, type: "sawtooth", dur: 0.4, color: "#32CD32" },
  { name: "Clap", emoji: "👏", freq: 1000, type: "square", dur: 0.08, color: "#FF8C00" },
];

const XYLOPHONE = [
  { note: "C", freq: 523, color: "#FF0000", label: "Do" },
  { note: "D", freq: 587, color: "#FF8C00", label: "Re" },
  { note: "E", freq: 659, color: "#FFD700", label: "Mi" },
  { note: "F", freq: 698, color: "#32CD32", label: "Fa" },
  { note: "G", freq: 784, color: "#1E90FF", label: "Sol" },
  { note: "A", freq: 880, color: "#8A2BE2", label: "La" },
  { note: "B", freq: 988, color: "#FF1493", label: "Si" },
  { note: "C2", freq: 1047, color: "#FF0000", label: "Do'" },
];

const playNote = (freq, type = "sine", duration = 0.4, volume = 0.3) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = type; osc.frequency.value = freq;
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + duration);
  } catch (err) { logger.debug("game op failed", err); }
};

const GameMusik = ({ onBackToMenu }) => {
  const { width: winW } = useWindowSize();
  const [instrument, setInstrument] = useState("piano");
  const [activeKey, setActiveKey] = useState(null);
  const [soundOn, setSoundOn] = useState(true);

  const handlePlay = useCallback((key, freq, type = "sine", dur = 0.4) => {
    if (!soundOn) return;
    playNote(freq, type, dur);
    setActiveKey(key);
    setTimeout(() => setActiveKey(null), 200);
  }, [soundOn]);

  const whiteKeys = PIANO_KEYS.filter(k => k.color === "white");
  const blackKeys = PIANO_KEYS.filter(k => k.color === "black");
  const keyW = Math.min((winW - 40) / whiteKeys.length, 70);

  return (
    <div className="flex flex-col items-center w-full max-w-5xl mx-auto px-3 pt-3" data-testid="game-musik">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <span className="font-fredoka text-gold font-bold text-sm">🎵 Musik</span>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={onBackToMenu} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      {/* Instrument tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { id: "piano", label: "🎹 Piano" },
          { id: "drum", label: "🥁 Drum" },
          { id: "xylo", label: "🎶 Xylophone" },
        ].map(tab => (
          <Button key={tab.id} data-testid={`musik-tab-${tab.id}`} onClick={() => setInstrument(tab.id)}
            className={`font-fredoka text-sm rounded-xl ${instrument === tab.id ? 'bg-gold text-brown-dark' : 'glass-card text-gold'}`}>
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Piano */}
      {instrument === "piano" && (
        <div className="relative mx-auto mb-4" style={{ width: whiteKeys.length * keyW }}>
          {/* White keys */}
          <div className="flex">
            {whiteKeys.map((key, i) => (
              <motion.button key={key.note} data-testid={`musik-piano-${key.note}`}
                onMouseDown={() => handlePlay(key.note, key.freq)}
                onTouchStart={(e) => { e.preventDefault(); handlePlay(key.note, key.freq); }}
                className={`border border-gray-300 rounded-b-lg flex flex-col items-center justify-end pb-2 relative z-0 ${activeKey === key.note ? 'bg-yellow-200' : 'bg-white'}`}
                style={{ width: keyW, height: keyW * 3.5 }}
                whileTap={{ scale: 0.97, backgroundColor: "#FFF3CD" }}>
                <span className="font-fredoka text-xs text-gray-500 font-bold">{key.label}</span>
              </motion.button>
            ))}
          </div>
          {/* Black keys */}
          <div className="absolute top-0 left-0 flex pointer-events-none" style={{ width: whiteKeys.length * keyW }}>
            {PIANO_KEYS.map((key, i) => {
              if (key.color !== "black") return null;
              const whiteIdx = PIANO_KEYS.slice(0, i).filter(k => k.color === "white").length;
              return (
                <motion.button key={key.note}
                  onMouseDown={() => handlePlay(key.note, key.freq)}
                  onTouchStart={(e) => { e.preventDefault(); handlePlay(key.note, key.freq); }}
                  className={`absolute rounded-b-md z-10 pointer-events-auto ${activeKey === key.note ? 'bg-gray-600' : 'bg-gray-900'}`}
                  style={{ width: keyW * 0.6, height: keyW * 2.2, left: whiteIdx * keyW - keyW * 0.3 }}
                  whileTap={{ scale: 0.95 }} />
              );
            })}
          </div>
        </div>
      )}

      {/* Drum */}
      {instrument === "drum" && (
        <div className="grid grid-cols-3 gap-3 max-w-md mx-auto">
          {DRUMS.map(d => (
            <motion.button key={d.name} data-testid={`musik-drum-${d.name}`}
              onMouseDown={() => handlePlay(d.name, d.freq, d.type, d.dur)}
              onTouchStart={(e) => { e.preventDefault(); handlePlay(d.name, d.freq, d.type, d.dur); }}
              className={`rounded-2xl p-4 text-center btn-3d ${activeKey === d.name ? 'brightness-125' : ''}`}
              style={{ background: d.color }}
              whileTap={{ scale: 0.85 }}>
              <span className="text-4xl block mb-1">{d.emoji}</span>
              <span className="font-fredoka text-white text-xs font-bold">{d.name}</span>
            </motion.button>
          ))}
        </div>
      )}

      {/* Xylophone */}
      {instrument === "xylo" && (
        <div className="flex gap-1 items-end justify-center mx-auto">
          {XYLOPHONE.map((bar, i) => {
            const barH = 120 + i * 15;
            return (
              <motion.button key={bar.note} data-testid={`musik-xylo-${bar.note}`}
                onMouseDown={() => handlePlay(bar.note, bar.freq, "sine", 0.6)}
                onTouchStart={(e) => { e.preventDefault(); handlePlay(bar.note, bar.freq, "sine", 0.6); }}
                className={`rounded-lg flex flex-col items-center justify-end pb-2 ${activeKey === bar.note ? 'brightness-125' : ''}`}
                style={{ width: Math.min(50, (winW - 60) / 8), height: barH, background: bar.color }}
                whileTap={{ scale: 0.93 }}>
                <span className="font-fredoka text-white text-xs font-bold drop-shadow">{bar.label}</span>
              </motion.button>
            );
          })}
        </div>
      )}

      <p className="font-fredoka text-gold-light/40 text-xs mt-4 text-center">Tap atau klik untuk bermain musik!</p>
    </div>
  );
};

export default GameMusik;
